import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import R from "ramda";
import queryString from "query-string";
import * as actionCreators from "../actions";
import getDisplayName from "../utils/getDisplayName";
import { setFieldOptions } from "../utils/fieldOptions";
import convertToAppDataFromContentful from "../utils/convertToAppDataFromContentful";
import convertToAppData from "../utils/convertToAppData";

const handleFieldOptions = options => {
  const {
    status,
    data: { code, content }
  } = options;
  if (status === 200 && code === 0) {
    setFieldOptions(content);
  }
};

const handleCountries = countries => {
  const {
    status,
    data: { code, content }
  } = countries;
  if (status === 200 && code === 0) {
    const fieldOptions = [];
    const countriesOption = R.map(country => {
      // Currently, only Chinese adminstrative divisions are needed.
      if (!R.isNil(country.children) && !R.isEmpty(country.children)) {
        fieldOptions.push({
          name: `adminDivisions${country.twoCode}`,
          options: country.children
        });
        fieldOptions.push({
          name: `city${country.twoCode}`,
          options: R.map(
            province => ({
              ...province,
              children: R.map(
                city => R.omit("children", city),
                province.children
              )
            }),
            country.children
          )
        });
      }
      // Handle country option.
      return {
        label: country.label,
        value: country.threeCode,
        group: country.type === "hot" ? "Popular Countries" : "All countries",
        icon: `flag-icon flag-icon-${country.twoCode.toLowerCase()}`
      };
    }, content);
    fieldOptions.push({
      name: "country",
      options: countriesOption
    });
    setFieldOptions(fieldOptions);
  }
};

const connectApplication = DataSource => WrappedComponent => {
  class ConnectApplication extends Component {
    constructor(props) {
      super(props);
      const { location } = props;
      const queryStr = queryString.parse(location.search);
      this.state = {
        isPreview: queryStr.preview === "true",
        isLoading: undefined,
        error: "",
        currentProgress: {}
      };
    }

    componentDidMount() {
      const {
        match: {
          params: { applyId }
        }
      } = this.props;
      this.setState({ isLoading: true });

      Promise.all([
        DataSource.getFieldOptions(),
        DataSource.getCountries(),
        DataSource.getApplication(applyId, this.state.isPreview)
      ])
        .then(
          ([options, countries, application]) => {
            handleFieldOptions(options);
            handleCountries(countries);
            let convertedAppData;
            let error;
            if (this.state.isPreview) {
              const { items = [] } = application;
              const { fields = [] } = items[0];
              if (R.not(R.isEmpty(fields))) {
                convertedAppData = convertToAppDataFromContentful(fields);
              } else {
                error = "retrieve application fail";
              }
            } else {
              const { code, content, msg } = application;
              if (code === 0) {
                convertedAppData = convertToAppData(content);
              } else {
                error = msg || code;
              }
            }

            if (convertedAppData) {
              const { currentProgress = {}, name } = convertedAppData;
              this.props.dispatchSetupApplication(convertedAppData, name);
              this.setState({ isLoading: false, currentProgress });
            } else {
              this.setState({
                isLoading: false,
                error
              });
            }
          },
          err => this.setState({ isLoading: false, error: err })
        )
        .catch(reason => {
          console.log("connectApplication DataSource fail >>", reason);
        });
    }

    componentWillUnmount() {
      this.props.dispatchDestroyApplication();
    }

    render() {
      return <WrappedComponent injectedProps={this.state} {...this.props} />;
    }
  }

  ConnectApplication.displayName = `ConnectApplication(${getDisplayName(
    WrappedComponent
  )})`;

  ConnectApplication.propTypes = {
    match: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    dispatchSetupApplication: PropTypes.func.isRequired,
    dispatchDestroyApplication: PropTypes.func.isRequired
  };

  const mapDispatchToProps = {
    dispatchSetupApplication: actionCreators.setupApplication,
    dispatchDestroyApplication: actionCreators.destroyApplication
  };

  return connect(null, mapDispatchToProps)(ConnectApplication);
};

export default connectApplication;
